import Bugsnag from '../lib/bugsnag';

// The SSO/user backend occasionally responds with a non-JSON body (empty
// response, HTML error page, gateway error). Calling response.json() directly
// then throws "SyntaxError: The string did not match the expected pattern" and
// bubbles up as an unhandled rejection. Parse defensively and return an empty
// object so callers fall through their `data.xxx` checks instead of crashing.
async function parseJsonSafe(response: Response) {
  const text = await response.text()
  try {
    return text ? JSON.parse(text) : {}
  } catch (e) {
    Bugsnag.notify(e as Error, event => {
      event.addMetadata('sso', { status: response.status, url: response.url, body: text.substring(0, 500) })
    })
    return {}
  }
}

export async function externalLogin(username: string, password: string) {
  const url = `${process.env.SSO_BACKEND_URL}/json/authenticate.php?callback=loginCallback&action=authenticate&userLogin=${encodeURIComponent(username)}&userPass=${encodeURIComponent(password)}`
  const res = await fetch(url)  
  const text = await res.text()
  return text.substring(14,text.length - 1).replace(/\\n/g, ' ')
}

export async function externalRefreshToken(refreshToken: string) {
  const url = `${process.env.SSO_BACKEND_URL}/json/authenticate.php?callback=loginCallback&action=refresh&refreshToken=${refreshToken}`
  const res = await fetch(url)
  
  const text = await res.text()
  return text.substring(14,text.length - 1).replace(/\\n/g, ' ')
}

export async function externalRegister(username: string) {
  const url = `${process.env.SSO_BACKEND_URL}/json/api.php`
  const response = await fetch(url, {
    method: 'POST', // *GET, POST, PUT, DELETE, etc.
    mode: 'cors', // no-cors, *cors, same-origin
    cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
    credentials: 'same-origin', // include, *same-origin, omit
    headers: {
      'Content-Type': 'application/json'
      // 'Content-Type': 'application/x-www-form-urlencoded',
    },
    redirect: 'follow', // manual, *follow, error
    referrerPolicy: 'no-referrer', // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
    body: JSON.stringify({
      method: "createUser",
      appKey: process.env.SSO_KEY,
      userLogin: username,
      userEmail: username,
      sendActivateLink: "sso-register",
      activateLink: process.env.SSO_ACTIVATE_BASE_URL + "/register/show-activate?token=[token]", // body data type must match "Content-Type" header
      activatePayload: { r: process.env.SSO_ACTIVATE_BASE_URL}})
  });
  const text = await response.text()
  return text
}

export async function externalUpdateUser(tokenId: string) {
  const url = `${process.env.SSO_BACKEND_URL}/json/api.php`
  const response = await fetch(url, {
    method: 'POST', // *GET, POST, PUT, DELETE, etc.
    mode: 'cors', // no-cors, *cors, same-origin
    cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
    credentials: 'same-origin', // include, *same-origin, omit
    headers: {
      'Content-Type': 'application/json'
      // 'Content-Type': 'application/x-www-form-urlencoded',
    },
    redirect: 'follow', // manual, *follow, error
    referrerPolicy: 'no-referrer', // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
    body: JSON.stringify({
      method: "getUserData",
      appKey: process.env.SSO_KEY,
      tokenId: tokenId
    })
  });
  const text = await response.text()
  return text
}

export async function login(username: string, password: string) {
  // Default options are marked with *
  const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/api/user/login`, {
    method: 'POST', // *GET, POST, PUT, DELETE, etc.
    mode: 'cors', // no-cors, *cors, same-origin
    cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
    credentials: 'same-origin', // include, *same-origin, omit
    headers: {
      'Content-Type': 'application/json'
      // 'Content-Type': 'application/x-www-form-urlencoded',
    },
    redirect: 'follow', // manual, *follow, error
    referrerPolicy: 'no-referrer', // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
    body: JSON.stringify({username: username, password: password}) // body data type must match "Content-Type" header
  });
  const json = await parseJsonSafe(response)
  return json
}

export async function update(tokenId: string) {
  // Default options are marked with *
  const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/api/user/update`, {
    method: 'POST', // *GET, POST, PUT, DELETE, etc.
    mode: 'cors', // no-cors, *cors, same-origin
    cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
    credentials: 'same-origin', // include, *same-origin, omit
    headers: {
      'Content-Type': 'application/json'
      // 'Content-Type': 'application/x-www-form-urlencoded',
    },
    redirect: 'follow', // manual, *follow, error
    referrerPolicy: 'no-referrer', // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
    body: JSON.stringify({tokenId: tokenId}) // body data type must match "Content-Type" header
  });
  const json = await parseJsonSafe(response)
  return json
}

export async function loginRefresh(refreshToken: string) {
  // Default options are marked with *
  const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/api/user/loginRefresh`, {
    method: 'POST', // *GET, POST, PUT, DELETE, etc.
    mode: 'cors', // no-cors, *cors, same-origin
    cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
    credentials: 'same-origin', // include, *same-origin, omit
    headers: {
      'Content-Type': 'application/json'
      // 'Content-Type': 'application/x-www-form-urlencoded',
    },
    redirect: 'follow', // manual, *follow, error
    referrerPolicy: 'no-referrer', // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
    body: JSON.stringify({refreshToken: refreshToken}) // body data type must match "Content-Type" header
  });
  const json = await parseJsonSafe(response)
  return json
}

export async function register(username: string) {
  // Default options are marked with *
  const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/api/user/register`, {
    method: 'POST', // *GET, POST, PUT, DELETE, etc.
    mode: 'cors', // no-cors, *cors, same-origin
    cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
    credentials: 'same-origin', // include, *same-origin, omit
    headers: {
      'Content-Type': 'application/json'
      // 'Content-Type': 'application/x-www-form-urlencoded',
    },
    redirect: 'follow', // manual, *follow, error
    referrerPolicy: 'no-referrer', // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
    body: JSON.stringify({username: username}) // body data type must match "Content-Type" header
  });
  const json = await parseJsonSafe(response)
  return json
}